import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  Typography,
  Box,
  Chip,
  Alert,
  AlertTitle,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
} from '@mui/material';
import HighQuality from '@mui/icons-material/HighQuality';
import Lock from '@mui/icons-material/Lock';
import CheckCircle from '@mui/icons-material/CheckCircle';
import Info from '@mui/icons-material/Info';
import { useAuth } from '../context/AuthContext';
import { usePlayer } from '../lib/PlayerContext';
import { AudioQuality, SubscriptionTier } from '../types/models';
import {
  AUDIO_QUALITY_INFO,
  QUALITY_LABELS,
  SUBSCRIPTION_TIER_LABELS,
  canAccessQuality,
  getAvailableQualities,
} from '../constants/audioQuality';

interface AudioQualitySettingsProps {
  open: boolean;
  onClose: () => void;
}

export function AudioQualitySettings({ open, onClose }: AudioQualitySettingsProps) {
  const { user } = useAuth();
  const { audioQuality, setAudioQuality, currentSong, getAvailableQualities: getSongQualities } = usePlayer();
  const [selectedQuality, setSelectedQuality] = useState<AudioQuality>(audioQuality);

  const userTier: SubscriptionTier = user?.subscriptionTier || 'free';
  const availableQualities = getAvailableQualities(userTier);
  const currentSongQualities = currentSong ? getSongQualities(currentSong) : [];

  const handleQualityChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const quality = event.target.value as AudioQuality;
    setSelectedQuality(quality);
  };

  const handleSave = () => {
    setAudioQuality(selectedQuality);
    onClose();
  };

  const getQualityIcon = (quality: AudioQuality) => {
    const isAccessible = canAccessQuality(userTier, quality);
    
    if (!isAccessible) {
      return <Lock sx={{ color: '#7A7A85' }} />;
    }
    
    if (quality === audioQuality) {
      return <CheckCircle sx={{ color: '#FF4D8C' }} />;
    }
    
    return <HighQuality sx={{ color: '#B8B8C0' }} />;
  };

  const getQualityDescription = (quality: AudioQuality) => {
    const info = AUDIO_QUALITY_INFO[quality];
    const parts = [info.bitrate, info.format, info.sampleRate];
    if (info.bitDepth) parts.push(info.bitDepth);
    return parts.join(' • ');
  };

  const getUpgradeMessage = () => {
    switch (userTier) {
      case 'free':
        return 'Upgrade to Plus for 320kbps quality, or Premium for lossless Hi-Res audio.';
      case 'plus':
        return 'Upgrade to Premium for lossless Hi-Res audio quality.';
      default:
        return null;
    }
  };

  return (
    <Dialog 
      open={open} 
      onClose={onClose} 
      maxWidth="sm" 
      fullWidth
      PaperProps={{
        sx: { 
          borderRadius: '12px',
          backgroundColor: '#1A1A1F',
          border: '1px solid #3A3A42',
          color: '#F8F9FA',
          boxShadow: '0 14px 28px rgba(0, 0, 0, 0.25), 0 10px 10px rgba(0, 0, 0, 0.22), 0 0 40px rgba(255, 77, 140, 0.1)',
        }
      }}
    >
      <DialogTitle sx={{ 
        color: '#F8F9FA', 
        borderBottom: '1px solid #3A3A42',
        backgroundColor: '#242429',
        padding: '20px 24px',
      }}>
        <Box display="flex" alignItems="center" gap={1}>
          <HighQuality sx={{ color: '#FF4D8C' }} />
          <span className="text-pearl-white font-outfit font-medium text-lg">Audio Quality Settings</span>
        </Box>
      </DialogTitle>
      
      <DialogContent sx={{ 
        backgroundColor: '#1A1A1F', 
        color: '#F8F9FA',
        padding: '24px',
      }}>
        <Box display="flex" flexDirection="column" gap={3}>
          {/* Current Subscription Info */}
          <Box>
            <Typography variant="subtitle2" gutterBottom sx={{ color: '#B8B8C0', fontFamily: 'Inter', fontSize: '14px', fontWeight: 500 }}>
              Current Subscription
            </Typography>
            <Box display="flex" alignItems="center" gap={1}>
              <Chip 
                label={SUBSCRIPTION_TIER_LABELS[userTier]}
                sx={{
                  backgroundColor: userTier === 'free' ? '#3A3A42' : '#FF4D8C',
                  color: userTier === 'free' ? '#B8B8C0' : '#F8F9FA',
                  fontFamily: 'Inter',
                  fontSize: '12px',
                  fontWeight: 500,
                  '&:hover': {
                    backgroundColor: userTier === 'free' ? '#3A3A42' : '#FF6B9F',
                  }
                }}
                size="small"
              />
              <Typography variant="body2" sx={{ color: '#7A7A85', fontFamily: 'Inter', fontSize: '13px' }}>
                Max quality: {QUALITY_LABELS[availableQualities[availableQualities.length - 1]]}
              </Typography>
            </Box>
          </Box>

          {/* Quality Selection */}
          <FormControl>
            <FormLabel component="legend" sx={{ color: '#B8B8C0', fontFamily: 'Inter', fontSize: '14px', fontWeight: 500, marginBottom: '8px' }}>
              Select Audio Quality
            </FormLabel>
            <RadioGroup
              value={selectedQuality}
              onChange={handleQualityChange}
              sx={{ mt: 1 }}
            >
              {Object.entries(QUALITY_LABELS).map(([quality, label]) => {
                const qualityKey = quality as AudioQuality;
                const isAccessible = canAccessQuality(userTier, qualityKey);
                const info = AUDIO_QUALITY_INFO[qualityKey];
                const isAvailableForCurrentSong = currentSongQualities.includes(qualityKey);

                return (
                  <FormControlLabel
                    key={quality}
                    value={quality}
                    disabled={!isAccessible}
                    control={
                      <Radio 
                        sx={{
                          color: '#7A7A85',
                          '&.Mui-checked': {
                            color: '#FF4D8C',
                          },
                          '&.Mui-disabled': {
                            color: '#3A3A42',
                          },
                          '&:hover': {
                            backgroundColor: 'rgba(255, 77, 140, 0.1)',
                          }
                        }}
                      />
                    }
                    label={
                      <Box display="flex" alignItems="center" justifyContent="space-between" width="100%">
                        <Box>
                          <Typography 
                            variant="body1" 
                            sx={{ 
                              color: isAccessible ? '#F8F9FA' : '#7A7A85',
                              fontFamily: 'Inter',
                              fontSize: '14px',
                              fontWeight: 500,
                            }}
                          >
                            {label}
                          </Typography>
                          <Typography 
                            variant="body2" 
                            sx={{ 
                              color: '#B8B8C0',
                              fontFamily: 'Inter',
                              fontSize: '12px',
                            }}
                          >
                            {getQualityDescription(qualityKey)}
                          </Typography>
                          {!isAccessible && info.requiresSubscription && (
                            <Typography 
                              variant="caption" 
                              sx={{ 
                                color: '#FFC107',
                                fontFamily: 'Inter',
                                fontSize: '11px',
                                fontStyle: 'italic',
                              }}
                            >
                              Requires {SUBSCRIPTION_TIER_LABELS[info.requiresSubscription]} subscription
                            </Typography>
                          )}
                          {currentSong && isAccessible && !isAvailableForCurrentSong && (
                            <Typography 
                              variant="caption" 
                              sx={{ 
                                color: '#7A7A85',
                                fontFamily: 'Inter',
                                fontSize: '11px',
                                fontStyle: 'italic',
                              }}
                            >
                              Not available for current song
                            </Typography>
                          )}
                        </Box>
                        {getQualityIcon(qualityKey)}
                      </Box>
                    }
                    sx={{
                      m: 0,
                      p: 2,
                      border: '1px solid #3A3A42',
                      borderRadius: '8px',
                      mb: 1,
                      backgroundColor: quality === selectedQuality ? 'rgba(255, 77, 140, 0.1)' : 'transparent',
                      '&:last-child': { mb: 0 },
                      '&.Mui-disabled': {
                        backgroundColor: '#242429',
                        opacity: 0.6,
                      },
                      '&:hover:not(.Mui-disabled)': {
                        backgroundColor: 'rgba(255, 77, 140, 0.05)',
                        borderColor: '#FF4D8C',
                      },
                      transition: 'all 0.2s ease',
                    }}
                  />
                );
              })}
            </RadioGroup>
          </FormControl>

          {/* Upgrade Prompt */}
          {getUpgradeMessage() && (
            <Alert 
              severity="info" 
              icon={<Info sx={{ color: '#4DE8FF' }} />}
              sx={{
                backgroundColor: 'rgba(77, 232, 255, 0.1)',
                border: '1px solid rgba(77, 232, 255, 0.3)',
                borderRadius: '8px',
                '& .MuiAlert-message': {
                  color: '#F8F9FA',
                  fontFamily: 'Inter',
                }
              }}
            >
              <AlertTitle sx={{ color: '#4DE8FF', fontFamily: 'Inter', fontWeight: 600 }}>Want Higher Quality?</AlertTitle>
              <Typography sx={{ color: '#B8B8C0', fontFamily: 'Inter', fontSize: '14px' }}>
                {getUpgradeMessage()}
              </Typography>
            </Alert>
          )}

          {/* Quality Comparison */}
          <Box>
            <Typography variant="subtitle2" gutterBottom sx={{ color: '#B8B8C0', fontFamily: 'Inter', fontSize: '14px', fontWeight: 500 }}>
              Quality Comparison
            </Typography>
            <List dense>
              <ListItem sx={{ padding: '8px 0' }}>
                <ListItemIcon sx={{ minWidth: '32px' }}>
                  <Typography variant="body2" sx={{ fontSize: '16px' }}>💾</Typography>
                </ListItemIcon>
                <ListItemText 
                  primary={
                    <Typography sx={{ color: '#F8F9FA', fontFamily: 'Inter', fontSize: '14px', fontWeight: 500 }}>
                      Storage Space
                    </Typography>
                  }
                  secondary={
                    <Typography sx={{ color: '#7A7A85', fontFamily: 'Inter', fontSize: '12px' }}>
                      Higher quality uses more storage for offline downloads
                    </Typography>
                  }
                />
              </ListItem>
              <ListItem sx={{ padding: '8px 0' }}>
                <ListItemIcon sx={{ minWidth: '32px' }}>
                  <Typography variant="body2" sx={{ fontSize: '16px' }}>📶</Typography>
                </ListItemIcon>
                <ListItemText 
                  primary={
                    <Typography sx={{ color: '#F8F9FA', fontFamily: 'Inter', fontSize: '14px', fontWeight: 500 }}>
                      Bandwidth Usage
                    </Typography>
                  }
                  secondary={
                    <Typography sx={{ color: '#7A7A85', fontFamily: 'Inter', fontSize: '12px' }}>
                      Higher quality uses more data when streaming
                    </Typography>
                  }
                />
              </ListItem>
              <ListItem sx={{ padding: '8px 0' }}>
                <ListItemIcon sx={{ minWidth: '32px' }}>
                  <Typography variant="body2" sx={{ fontSize: '16px' }}>🎧</Typography>
                </ListItemIcon>
                <ListItemText 
                  primary={
                    <Typography sx={{ color: '#F8F9FA', fontFamily: 'Inter', fontSize: '14px', fontWeight: 500 }}>
                      Audio Equipment
                    </Typography>
                  }
                  secondary={
                    <Typography sx={{ color: '#7A7A85', fontFamily: 'Inter', fontSize: '12px' }}>
                      Hi-Res quality benefits require high-quality headphones/speakers
                    </Typography>
                  }
                />
              </ListItem>
            </List>
          </Box>
        </Box>
      </DialogContent>

      <DialogActions sx={{ 
        backgroundColor: '#242429', 
        borderTop: '1px solid #3A3A42',
        padding: '16px 24px',
        gap: '12px',
      }}>
        <Button 
          onClick={onClose}
          sx={{
            color: '#B8B8C0',
            fontFamily: 'Inter',
            fontWeight: 500,
            textTransform: 'none',
            padding: '8px 16px',
            borderRadius: '8px',
            '&:hover': {
              backgroundColor: 'rgba(184, 184, 192, 0.1)',
              color: '#F8F9FA',
            }
          }}
        >
          Cancel
        </Button>
        <Button 
          onClick={handleSave} 
          variant="contained"
          disabled={selectedQuality === audioQuality}
          sx={{
            backgroundColor: '#FF4D8C',
            color: '#F8F9FA',
            fontFamily: 'Inter',
            fontWeight: 500,
            textTransform: 'none',
            padding: '8px 20px',
            borderRadius: '8px',
            boxShadow: '0 3px 6px rgba(0, 0, 0, 0.16), 0 3px 6px rgba(0, 0, 0, 0.23)',
            '&:hover': {
              backgroundColor: '#FF6B9F',
              boxShadow: '0 6px 12px rgba(0, 0, 0, 0.2), 0 0 20px rgba(255, 77, 140, 0.3)',
            },
            '&:disabled': {
              backgroundColor: '#3A3A42',
              color: '#7A7A85',
              boxShadow: 'none',
            }
          }}
        >
          Apply Settings
        </Button>
      </DialogActions>
    </Dialog>
  );
}